import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FileText, Settings, LogOut, Menu, X, Users, Sparkles, BarChart3, GraduationCap, PanelLeftClose, PanelLeftOpen, Home } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { isDemoUser } from "@/lib/demo";

// The Plan page is gated to the Elementar Sandbox demo tenant (see lib/demo.js).

const navItems = [
  { to: "/home", label: "Home", icon: Home },
  { to: "/documents", label: "My Documents", icon: FileText },
  { to: "/groups", label: "My Groups", icon: Users },
  { to: "/dashboard", label: "Ledger", icon: BarChart3 },
  { to: "/plan", label: "Plan", icon: Sparkles, demoOnly: true },
  { to: "/settings", label: "My Settings", icon: Settings },
];

/* Elementar icon — clean E lettermark on dark rounded square */
function ElementarIcon({ size = 36 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400" width={size} height={size}>
      <rect width="400" height="400" rx="48" fill="#0F0F0F" />
      <g transform="translate(200,200)" fill="none" stroke="#FFFFFF" strokeLinecap="round" strokeLinejoin="round">
        <line x1="-60" y1="-80" x2="-60" y2="80" strokeWidth="14" />
        <line x1="-60" y1="-80" x2="60" y2="-80" strokeWidth="14" />
        <line x1="-60" y1="0" x2="40" y2="0" strokeWidth="14" />
        <line x1="-60" y1="80" x2="60" y2="80" strokeWidth="14" />
      </g>
    </svg>
  );
}

// `collapsed` (icon-only rail) is desktop-only: the mobile Sheet never passes
// it or `onToggle`, so it always renders expanded without the toggle button.
function NavContent({ onNavigate, collapsed = false, onToggle }) {
  const { user, logout } = useAuth();
  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: "#C41E1E" }}>
      {/* Logo */}
      <div
        className={collapsed ? "py-6 px-3" : "p-6"}
        style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}
      >
        <div className={`flex items-center ${collapsed ? "flex-col gap-3" : "gap-3"}`}>
          <ElementarIcon size={collapsed ? 32 : 40} />
          {!collapsed && (
            <div>
              <span
                className="text-base font-bold tracking-widest"
                style={{ color: "#FFFFFF", fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "2px" }}
              >
                ELEMENTAR
              </span>
            </div>
          )}
          {onToggle && (
            <button
              type="button"
              onClick={onToggle}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              className={`${collapsed ? "" : "ml-auto"} p-1 rounded-md transition-colors`}
              style={{ color: "rgba(255,255,255,0.7)" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                e.currentTarget.style.color = "#FFFFFF";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "rgba(255,255,255,0.7)";
              }}
            >
              <ToggleIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1">
        {navItems
          .filter(({ demoOnly }) => !(demoOnly && !isDemoUser(user)))
          .map(({ to, label, icon: Icon, beta }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            aria-label={label}
            title={collapsed ? label : undefined}
            data-tour={
              to === "/home" ? "home"
              : to === "/dashboard" ? "dashboard"
              : to === "/documents" ? "documents"
              : to === "/groups" ? "funds"
              : to === "/plan" ? "plan"
              : undefined
            }
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors${
                collapsed ? " justify-center" : ""
              }`
            }
            style={({ isActive }) =>
              isActive
                ? {
                    backgroundColor: "rgba(255,255,255,0.12)",
                    color: "#FFFFFF",
                    borderLeft: "3px solid #FFFFFF",
                  }
                : {
                    color: "rgba(255,255,255,0.7)",
                  }
            }
            onMouseEnter={(e) => {
              if (!e.currentTarget.classList.contains("active")) {
                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                e.currentTarget.style.color = "#FFFFFF";
              }
            }}
            onMouseLeave={(e) => {
              const isActive = e.currentTarget.getAttribute("aria-current") === "page";
              if (!isActive) {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "rgba(255,255,255,0.7)";
              }
            }}
          >
            <Icon className="w-4 h-4 shrink-0" />
            {!collapsed && label}
            {!collapsed && beta && (
              <span
                className="ml-auto text-[10px] font-semibold rounded px-1.5 py-0.5 leading-none"
                style={{
                  backgroundColor: "rgba(255,255,255,0.15)",
                  color: "#FFFFFF",
                }}
              >
                BETA
              </span>
            )}
          </NavLink>
        ))}
        {/* Academy is a public server-rendered page, not a React route — a
            NavLink would fall into the SPA catch-all and redirect to /documents. */}
        <a
          href="/academy"
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavigate}
          aria-label="Academy"
          title={collapsed ? "Academy" : undefined}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors${
            collapsed ? " justify-center" : ""
          }`}
          style={{ color: "rgba(255,255,255,0.7)" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
            e.currentTarget.style.color = "#FFFFFF";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "rgba(255,255,255,0.7)";
          }}
        >
          <GraduationCap className="w-4 h-4 shrink-0" />
          {!collapsed && "Academy"}
        </a>
      </nav>

      {/* User info */}
      <div className={collapsed ? "py-4 px-2" : "p-4"} style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        <div className={`flex items-center gap-3${collapsed ? " justify-center" : ""}`}>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: "#FFFFFF" }}>
                {user?.name || "User"}
              </p>
              <p className="text-xs truncate" style={{ color: "rgba(255,255,255,0.5)" }}>
                {user?.email || ""}
              </p>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            className="shrink-0"
            style={{ color: "rgba(255,255,255,0.7)" }}
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Sidebar({ collapsed = false, onToggle }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:left-0 ${
          collapsed ? "lg:w-16" : "lg:w-64"
        } z-30 transition-[width] duration-200 overflow-hidden`}
        style={{ backgroundColor: "#C41E1E" }}
      >
        <NavContent collapsed={collapsed} onToggle={onToggle} />
      </aside>

      {/* Mobile hamburger button */}
      <div className="lg:hidden fixed top-4 left-4 z-40">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="shadow-sm"
              style={{ backgroundColor: "#C41E1E", color: "#FFFFFF", borderColor: "#C41E1E" }}
            >
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64" style={{ backgroundColor: "#C41E1E" }}>
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <NavContent onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
